import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { withTranslation } from "next-i18next"

import { apim } from "@/constants/api"
import { getConfig } from "@/constants/config"
import {
  getCountryList,
  getGenericListPath,
  getstateList,
  selectGenericState,
  showToast,
} from "@/store/features/genericSlice"
import {
  getSiteWideModal,
  mapStateToCountryHandler,
  sanitizeInnerHtml,
  sanitizeObject,
  validateField,
} from "@/utils/helper"
import { validateRecaptcha } from "@/components/core/ReCaptcha/ReCaptcha"
import ReCaptcha from "@/components/core/ReCaptcha/ReCaptcha"
import Input from "@/components/core/Input/Input"
import Dropdown from "@/components/core/SelectDropdown/SelectDropdown"
import UploadProjectFiles from "./UploadProjectFiles"
import emailIcon from "@/public/icons/email.svg"
import useProjectSubmissionsi18n from "@/i18n/useProjectSubmissionsi18n"
import textAreaStyles from "@/components/core/TextArea/index.module.scss"
import ProjectSubmissionConfirmation from "./Confirmation"
import { isValidProjectLink } from "./helper"
import { createProjectSubmissionPayload } from "@/utils/projectSubmissionPayload"
import {
  addConsentAnalytics,
  addFieldErrorAnalytics,
  addFormProgressAnalytics,
  addPageViewAnalytics,
  addSubmitAnalytics,
  addUploadAnalytics,
  PROJECT_SUBMISSION_SECTIONS,
} from "./analytics"

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  projectName: "",
  city: "",
  state: "",
  projectType: "",
  orderNumber: "",
  productsUsed: "",
  projectDescription: "",
  projectLink: "",
  designer: "",
  photographer: "",
  disclaimerAgreement: false,
}

const DEFAULT_LEGAL_PAGES = {
  googlePrivacyPolicy: "https://policies.google.com/privacy",
  googleTermsOfService: "https://policies.google.com/terms",
  privacyPolicy: "https://www.kohlercompany.com/privacy",
  subjectAccessRequest: "https://kohler.service-now.com/gdp/form.do",
  termsOfService: "https://www.kohlercompany.com/legal",
}

const Field = ({
  id,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  type = "text",
  prefix,
  maxLength,
  required = false,
  autoComplete,
}) => (
  <div className="project-submissions__field">
    <Input
      id={id}
      name={id}
      type={type}
      value={value}
      placeholder={label}
      label={label}
      prefix={
        prefix ? <img src={prefix} alt="" aria-hidden="true" /> : undefined
      }
      showError={Boolean(error)}
      errorMessage={error}
      required={required}
      autoComplete={autoComplete}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      aria-errormessage={error ? `${id}-error` : undefined}
      maxLength={maxLength}
      onChange={onChange}
      onBlur={onBlur}
      onFocus={onFocus}
    />
  </div>
)

const SectionHeading = ({ title, requiredText }) => (
  <>
    <legend className="sr-only">{title}</legend>
    <div className="project-submissions__section-heading">
      <h2>{title}</h2>
      {requiredText ? <span>{requiredText}</span> : null}
    </div>
  </>
)

const requiredLabel = label => (label?.includes("*") ? label : `${label}*`)

const ProjectSubmissions = ({ data = {} }) => {
  const dispatch = useDispatch()
  const { countryListPath, stateListPath } = useSelector(selectGenericState)
  const [form, setForm] = useState(INITIAL_FORM)
  const [stateList, setStateList] = useState([])
  const [files, setFiles] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const [legalPages, setLegalPages] = useState(DEFAULT_LEGAL_PAGES)
  const formStarted = useRef(false)
  const interactedFields = useRef(new Set())
  const reportedFieldErrors = useRef(new Map())
  const i18n = useProjectSubmissionsi18n(data)

  useEffect(() => {
    return addPageViewAnalytics()
  }, [])

  const trackFormInteraction = (section, field) => {
    if (!formStarted.current) {
      formStarted.current = true
      interactedFields.current.add(field)
      addFormProgressAnalytics(section, true)
      return
    }

    if (!interactedFields.current.has(field)) {
      interactedFields.current.add(field)
      addFormProgressAnalytics(section)
    }
  }

  const handleExternalLink = (event, url) => {
    getSiteWideModal(event, url)
  }

  useEffect(() => {
    dispatch(getGenericListPath())
  }, [dispatch])

  useEffect(() => {
    let isMounted = true
    getConfig()
      .then(config => {
        if (isMounted) {
          setLegalPages({
            ...DEFAULT_LEGAL_PAGES,
            ...config?.legalPageData,
          })
          setRecaptchaReady(
            Boolean(
              !config?.general?.isDisableRecaptcha &&
                config?.captcha?.captchaSiteKeyV2 &&
                config?.captcha?.captchaSiteKeyV3
            )
          )
        }
      })
      .catch(() => setRecaptchaReady(false))

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    const getLocations = async () => {
      if (!countryListPath || !stateListPath) return

      try {
        const [countryData, stateData, config] = await Promise.all([
          dispatch(getCountryList()),
          dispatch(getstateList()),
          getConfig(),
        ])
        const countries = mapStateToCountryHandler(
          countryData?.payload ?? [],
          stateData?.payload ?? []
        )
        const countryCode =
          config?.internationalization?.country?.toUpperCase() ?? "US"
        setStateList(
          countries.find(country => country?.code === countryCode)?.states ?? []
        )
      } catch (error) {
        setStateList([])
      }
    }

    getLocations()
  }, [countryListPath, dispatch, stateListPath])
  const projectTypes = useMemo(() => {
    const authoredOptions = Array.isArray(data.projectTypeOptions)
      ? data.projectTypeOptions.map(type => String(type).trim()).filter(Boolean)
      : []

    return authoredOptions.length
      ? authoredOptions
      : i18n.projectTypes
          .split(",")
          .map(type => type.trim())
          .filter(Boolean)
  }, [data.projectTypeOptions, i18n.projectTypes])
  const stateOptions = useMemo(() => {
    const options = stateList.map(state => ({
      label: state?.name ?? state?.code,
      value: state?.code ?? state?.name,
    }))
    const hasOther = options.some(
      option =>
        option.label?.toLowerCase() === "other" ||
        option.value?.toLowerCase() === "other"
    )

    return hasOther
      ? options
      : [...options, { label: i18n.other, value: "Other" }]
  }, [i18n.other, stateList])
  const selectedState =
    stateOptions.find(option => option.value === form.state)?.label ?? ""

  const requiredFields = [
    "firstName",
    "lastName",
    "email",
    "city",
    "state",
    "projectType",
  ]

  const validate = field => {
    const value = String(form[field] ?? "")
    const trimmedValue = value.trim()

    if (!trimmedValue) {
      const requiredErrors = {
        firstName: i18n.firstNameRequired,
        lastName: i18n.lastNameRequired,
        email: i18n.emailRequired,
        city: i18n.cityRequired,
        state: i18n.stateRequired,
        projectType: i18n.projectTypeRequired,
      }
      return requiredErrors[field] ?? ""
    }

    if (
      ["firstName", "lastName", "photographer"].includes(field) &&
      !/^[A-Za-z ]+$/.test(value)
    ) {
      return i18n.lettersAndSpacesError
    }
    if (field === "designer" && !/^[A-Za-z0-9 ]+$/.test(value)) {
      return i18n.lettersNumbersSpacesError
    }
    if (
      field === "email" &&
      !/^[A-Za-z0-9._-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value)
    ) {
      return i18n.validEmailError
    }
    if (field === "projectLink" && !isValidProjectLink(value)) {
      return i18n.validProjectLinkError
    }
    return ""
  }

  const validatedFields = [
    ...requiredFields,
    "projectLink",
    "designer",
    "photographer",
  ]
  const isFormIncomplete =
    !form.disclaimerAgreement ||
    validatedFields.some(field => Boolean(validate(field)))
  const isSubmitDisabled = !form.disclaimerAgreement
  const updateField = event => {
    const { name, value, type, checked } = event.target

    setForm(current => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }))
    if (errors[name]) {
      setErrors(current => ({ ...current, [name]: "" }))
    }
    reportedFieldErrors.current.delete(name)
  }

  const updateNameField = async event => {
    const { name, value } = event.target

    if (value === "" || (await validateField("name", value))) {
      updateField({ target: { name, value, type: "text" } })
    }
  }

  const onBlur = (field, section) => {
    const message = validate(field)
    setErrors(current => ({ ...current, [field]: message }))
    if (message && reportedFieldErrors.current.get(field) !== message) {
      reportedFieldErrors.current.set(field, message)
      addFieldErrorAnalytics({ section, message })
    } else if (!message) {
      reportedFieldErrors.current.delete(field)
    }
  }

  const notifyError = message => {
    dispatch(showToast({ message, isVisible: true }))
  }

  const resetForm = () => {
    files.forEach(file => {
      if (file?.preview) URL.revokeObjectURL(file.preview)
    })
    setForm(INITIAL_FORM)
    setFiles([])
    setErrors({})
    setLoading(false)
    setSubmitted(false)
    formStarted.current = false
    interactedFields.current = new Set()
    reportedFieldErrors.current = new Map()
  }

  const onSubmit = async event => {
    event.preventDefault()
    const nextErrors = validatedFields.reduce(
      (result, field) => ({ ...result, [field]: validate(field) }),
      {}
    )
    setErrors(nextErrors)
    const firstInvalidField = validatedFields.find(field => nextErrors[field])
    if (firstInvalidField) {
      addFieldErrorAnalytics({
        section:
          firstInvalidField === "designer" ||
          firstInvalidField === "photographer"
            ? PROJECT_SUBMISSION_SECTIONS.attribution
            : firstInvalidField === "city" ||
              firstInvalidField === "state" ||
              firstInvalidField === "projectType" ||
              firstInvalidField === "projectLink"
            ? PROJECT_SUBMISSION_SECTIONS.projectInformation
            : PROJECT_SUBMISSION_SECTIONS.contactInformation,
        message: Object.values(nextErrors).filter(Boolean).join(", "),
        internalLinkName: "submit",
      })
      window.requestAnimationFrame(() =>
        document.getElementById(firstInvalidField)?.focus()
      )
      return
    }

    setLoading(true)
    try {
      const recaptcha = await validateRecaptcha()
      if (!recaptcha?.success) throw new Error("Recaptcha validation failed")

      const payload = createProjectSubmissionPayload(form, files, {
        disclaimer: i18n.disclaimer,
      })
      sanitizeObject(payload)

      const response = await apim.post("/crm/marketing/email", payload, {
        params: { brand: "annsacks" },
      })
      addSubmitAnalytics({
        success: true,
        message: i18n.successTitle,
        orderID:
          response?.data?.orderID ??
          response?.data?.orderId ??
          response?.data?.OrderID ??
          response?.data?.OrderId ??
          form.orderNumber,
        projectType: form.projectType,
        projectLocation: `${form.city}, ${form.state}`,
      })
      setSubmitted(true)
    } catch (error) {
      addSubmitAnalytics({
        success: false,
        message:
          error?.response?.data?.message ?? error?.message ?? i18n.genericError,
      })
      notifyError(i18n.genericError)
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="project-submissions__success">
        <ProjectSubmissionConfirmation
          message={i18n.successTitle}
          description={i18n.successDescription}
          continueLabel={i18n.continueShopping}
          continueUrl="https://annsacks.kohler.com/"
        />
      </div>
    )
  }

  return (
    <div className="kf-react-container">
      <form className="project-submissions" onSubmit={onSubmit} noValidate>
        <fieldset>
          <SectionHeading
            title={i18n.contactInformation}
            requiredText={i18n.requiredText}
          />
          <div className="project-submissions__grid project-submissions__grid--two">
            <Field
              id="firstName"
              label={requiredLabel(i18n.firstName)}
              required
              autoComplete="given-name"
              value={form.firstName}
              onChange={updateNameField}
              onFocus={() =>
                trackFormInteraction(
                  PROJECT_SUBMISSION_SECTIONS.contactInformation,
                  "firstName"
                )
              }
              onBlur={() =>
                onBlur(
                  "firstName",
                  PROJECT_SUBMISSION_SECTIONS.contactInformation
                )
              }
              error={errors.firstName}
              maxLength={42}
            />
            <Field
              id="lastName"
              label={requiredLabel(i18n.lastName)}
              required
              autoComplete="family-name"
              value={form.lastName}
              onChange={updateNameField}
              onFocus={() =>
                trackFormInteraction(
                  PROJECT_SUBMISSION_SECTIONS.contactInformation,
                  "lastName"
                )
              }
              onBlur={() =>
                onBlur(
                  "lastName",
                  PROJECT_SUBMISSION_SECTIONS.contactInformation
                )
              }
              error={errors.lastName}
              maxLength={42}
            />
          </div>
          <Field
            id="email"
            type="email"
            label={requiredLabel(i18n.email)}
            required
            autoComplete="email"
            value={form.email}
            onChange={updateField}
            onFocus={() =>
              trackFormInteraction(
                PROJECT_SUBMISSION_SECTIONS.contactInformation,
                "email"
              )
            }
            onBlur={() =>
              onBlur("email", PROJECT_SUBMISSION_SECTIONS.contactInformation)
            }
            error={errors.email}
            prefix={emailIcon?.src}
            maxLength={70}
          />
          <Field
            id="company"
            label={i18n.company}
            autoComplete="organization"
            value={form.company}
            onChange={updateField}
            onFocus={() =>
              trackFormInteraction(
                PROJECT_SUBMISSION_SECTIONS.contactInformation,
                "company"
              )
            }
            maxLength={70}
          />
        </fieldset>

        <fieldset className="project-submissions__section">
          <SectionHeading
            title={i18n.projectInformation}
            requiredText={i18n.requiredText}
          />
          <Field
            id="projectName"
            label={i18n.projectName}
            value={form.projectName}
            onChange={updateField}
            onFocus={() =>
              trackFormInteraction(
                PROJECT_SUBMISSION_SECTIONS.projectInformation,
                "projectName"
              )
            }
            maxLength={70}
          />
          <fieldset className="project-submissions__location">
            <legend className="project-submissions__sub-label">
              {i18n.projectLocation}
            </legend>
            <div className="project-submissions__grid project-submissions__grid--location">
              <Field
                id="city"
                label={requiredLabel(i18n.city)}
                required
                autoComplete="address-level2"
                value={form.city}
                onChange={updateField}
                onFocus={() =>
                  trackFormInteraction(
                    PROJECT_SUBMISSION_SECTIONS.projectInformation,
                    "city"
                  )
                }
                onBlur={() =>
                  onBlur("city", PROJECT_SUBMISSION_SECTIONS.projectInformation)
                }
                error={errors.city}
                maxLength={70}
              />
              <div className="project-submissions__field">
                <Dropdown
                  id="state"
                  data={stateOptions.map(option => option.label)}
                  value={selectedState}
                  label={requiredLabel(i18n.state)}
                  showError={Boolean(errors.state)}
                  errorMessage={errors.state}
                  ariaInvalid={Boolean(errors.state)}
                  onChange={label => {
                    trackFormInteraction(
                      PROJECT_SUBMISSION_SECTIONS.projectInformation,
                      "state"
                    )
                    const state = stateOptions.find(
                      option => option.label === label
                    )
                    updateField({
                      target: {
                        name: "state",
                        value: state?.value ?? label,
                      },
                    })
                  }}
                  onClose={() =>
                    onBlur(
                      "state",
                      PROJECT_SUBMISSION_SECTIONS.projectInformation
                    )
                  }
                />
              </div>
            </div>
          </fieldset>
          <div className="project-submissions__field">
            <Dropdown
              id="projectType"
              data={projectTypes}
              value={form.projectType}
              label={requiredLabel(i18n.projectType)}
              showError={Boolean(errors.projectType)}
              errorMessage={errors.projectType}
              ariaInvalid={Boolean(errors.projectType)}
              onChange={value => {
                trackFormInteraction(
                  PROJECT_SUBMISSION_SECTIONS.projectInformation,
                  "projectType"
                )
                updateField({
                  target: {
                    name: "projectType",
                    value,
                  },
                })
              }}
              onClose={() =>
                onBlur(
                  "projectType",
                  PROJECT_SUBMISSION_SECTIONS.projectInformation
                )
              }
            />
          </div>
          <Field
            id="orderNumber"
            label={i18n.orderNumber}
            value={form.orderNumber}
            onChange={updateField}
            onFocus={() =>
              trackFormInteraction(
                PROJECT_SUBMISSION_SECTIONS.projectInformation,
                "orderNumber"
              )
            }
            maxLength={70}
          />
          <Field
            id="productsUsed"
            label={i18n.productsUsed}
            value={form.productsUsed}
            onChange={updateField}
            onFocus={() =>
              trackFormInteraction(
                PROJECT_SUBMISSION_SECTIONS.projectInformation,
                "productsUsed"
              )
            }
            maxLength={70}
          />
          <div
            className={`${textAreaStyles.wrapper} project-submissions__textarea`}
          >
            <div className="textarea__wrapper">
              <textarea
                className="textarea-message"
                id="projectDescription"
                name="projectDescription"
                value={form.projectDescription}
                placeholder={i18n.projectDescription}
                aria-describedby={
                  form.projectDescription.length
                    ? "project-description-count"
                    : undefined
                }
                maxLength={500}
                onChange={updateField}
                onFocus={() =>
                  trackFormInteraction(
                    PROJECT_SUBMISSION_SECTIONS.projectInformation,
                    "projectDescription"
                  )
                }
              />
              <label htmlFor="projectDescription">
                {i18n.projectDescription}
              </label>
              {form.projectDescription.length ? (
                <span
                  id="project-description-count"
                  className="project-submissions__character-count"
                  aria-label={`${form.projectDescription.length} ${i18n.charactersUsed} 500`}
                >
                  {form.projectDescription.length}/500
                </span>
              ) : null}
            </div>
          </div>
          <Field
            id="projectLink"
            type="url"
            label={i18n.projectLink}
            value={form.projectLink}
            onChange={updateField}
            onFocus={() =>
              trackFormInteraction(
                PROJECT_SUBMISSION_SECTIONS.projectInformation,
                "projectLink"
              )
            }
            onBlur={() =>
              onBlur(
                "projectLink",
                PROJECT_SUBMISSION_SECTIONS.projectInformation
              )
            }
            error={errors.projectLink}
          />
        </fieldset>

        <fieldset className="project-submissions__section">
          <SectionHeading title={i18n.attribution} />
          <Field
            id="designer"
            label={i18n.designer}
            value={form.designer}
            onChange={updateField}
            onFocus={() =>
              trackFormInteraction(
                PROJECT_SUBMISSION_SECTIONS.attribution,
                "designer"
              )
            }
            onBlur={() =>
              onBlur("designer", PROJECT_SUBMISSION_SECTIONS.attribution)
            }
            error={errors.designer}
            maxLength={42}
          />
          <Field
            id="photographer"
            label={i18n.photographer}
            value={form.photographer}
            onChange={updateField}
            onFocus={() =>
              trackFormInteraction(
                PROJECT_SUBMISSION_SECTIONS.attribution,
                "photographer"
              )
            }
            onBlur={() =>
              onBlur("photographer", PROJECT_SUBMISSION_SECTIONS.attribution)
            }
            error={errors.photographer}
            maxLength={42}
          />
        </fieldset>

        <section
          className="project-submissions__section project-submissions__upload-section"
          aria-labelledby="project-upload-title"
        >
          <h2 id="project-upload-title">{i18n.uploadTitle}</h2>
          <p>{i18n.uploadCount}</p>
          <p id="project-upload-instructions">
            {i18n.uploadFormats}
            <br />
            {i18n.uploadSize}
          </p>
          <UploadProjectFiles
            imageList={files}
            updateList={setFiles}
            instructionsId="project-upload-instructions"
            onInteraction={() =>
              trackFormInteraction(
                PROJECT_SUBMISSION_SECTIONS.projectDocuments,
                "projectDocuments"
              )
            }
            onUploadInitiated={() => addUploadAnalytics("initiated")}
            onUploadSuccess={() => addUploadAnalytics("success")}
            onUploadError={message =>
              addFieldErrorAnalytics({
                section: PROJECT_SUBMISSION_SECTIONS.projectDocuments,
                message,
              })
            }
            i18n={{
              uploadCountLimit: i18n.uploadCount,
              errorFileType: i18n.uploadFileTypeError,
              errorMaxFileSizeLimit: i18n.uploadFileSizeError,
              dragAndDrop: i18n.dragAndDrop,
              orText: i18n.or,
              uploadFileButton: i18n.uploadFile,
              uploadingFiles: i18n.uploadingFiles,
              uploadFailed: i18n.uploadFailed,
              filesUploaded: i18n.filesUploaded,
              removeFile: i18n.removeFile,
              fileRemoved: i18n.fileRemoved,
              ariaLabels: {
                imageRemove: i18n.imageRemoved,
              },
            }}
          />
        </section>

        <section className="project-submissions__disclaimer">
          <p>{i18n.disclaimer}</p>
          <label htmlFor="disclaimerAgreement">
            <input
              id="disclaimerAgreement"
              name="disclaimerAgreement"
              type="checkbox"
              checked={form.disclaimerAgreement}
              required
              onChange={event => {
                updateField(event)
                addConsentAnalytics({
                  accepted: event.target.checked,
                  details: `${i18n.disclaimer}, ${
                    data.disclaimerText || i18n.agreement
                  }`,
                })
              }}
            />
            <span aria-hidden="true" />
            {sanitizeInnerHtml(data.disclaimerText || i18n.agreement)}
          </label>
        </section>

        <p id="project-submit-help" className="sr-only">
          {isFormIncomplete ? i18n.submitIncompleteHelp : i18n.submitReadyHelp}
        </p>
        <button
          className="project-submissions__submit"
          type="submit"
          disabled={loading || isSubmitDisabled}
          aria-describedby="project-submit-help"
        >
          {loading ? i18n.submitting : i18n.submit}
        </button>

        {recaptchaReady ? (
          <ReCaptcha
            getAnalyticsData={{
              addNoFooterInAttr: true,
              pageTitle: "project submissions",
              internalLinkPosition: "project submissions",
              internalLinkType: "form:navigation",
            }}
          />
        ) : (
          <div className="project-submissions__recaptcha-copy">
            <span id="project-external-link-help" className="sr-only">
              {i18n.opensInNewTab}
            </span>
            <span>{`${i18n.protectedByGoogle} `}</span>
            {legalPages?.googlePrivacyPolicy ? (
              <a
                href={legalPages.googlePrivacyPolicy}
                target="_blank"
                rel="noopener noreferrer"
                aria-describedby="project-external-link-help"
                onClick={event =>
                  handleExternalLink(event, legalPages.googlePrivacyPolicy)
                }
              >
                {i18n.googlePrivacyPolicy}
              </a>
            ) : null}
            <span>{` ${i18n.and} `}</span>
            {legalPages?.googleTermsOfService ? (
              <a
                href={legalPages.googleTermsOfService}
                target="_blank"
                rel="noopener noreferrer"
                aria-describedby="project-external-link-help"
                onClick={event =>
                  handleExternalLink(event, legalPages.googleTermsOfService)
                }
              >
                {i18n.termsOfService}
              </a>
            ) : null}
            <span>{` ${i18n.apply}`}</span>
          </div>
        )}
        <nav
          className="project-submissions__policies"
          aria-label="Form policies"
        >
          <span id="project-policy-link-help" className="sr-only">
            {i18n.opensInNewTab}
          </span>
          {data.privacypolicylink || legalPages?.privacyPolicy ? (
            <a
              href={data.privacypolicylink || legalPages?.privacyPolicy}
              target="_blank"
              rel="noopener noreferrer"
              aria-describedby="project-policy-link-help"
              onClick={event =>
                handleExternalLink(
                  event,
                  data.privacypolicylink || legalPages?.privacyPolicy
                )
              }
            >
              {i18n.privacyPolicy}
            </a>
          ) : null}
          {data.subjectaccessrequestlink || legalPages?.subjectAccessRequest ? (
            <a
              href={
                data.subjectaccessrequestlink ||
                legalPages?.subjectAccessRequest
              }
              target="_blank"
              rel="noopener noreferrer"
              aria-describedby="project-policy-link-help"
              onClick={event =>
                handleExternalLink(
                  event,
                  data.subjectaccessrequestlink ||
                    legalPages?.subjectAccessRequest
                )
              }
            >
              {i18n.subjectAccessRequest}
            </a>
          ) : null}
          {data.termsofservicelink || legalPages?.termsOfService ? (
            <a
              href={data.termsofservicelink || legalPages?.termsOfService}
              target="_blank"
              rel="noopener noreferrer"
              aria-describedby="project-policy-link-help"
              onClick={event =>
                handleExternalLink(
                  event,
                  data.termsofservicelink || legalPages?.termsOfService
                )
              }
            >
              {i18n.termsOfService}
            </a>
          ) : null}
        </nav>
      </form>
    </div>
  )
}

export default withTranslation()(ProjectSubmissions)
